import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikErrors, FormikTouched } from 'formik';
import classnames from 'classnames';
import styled from 'styled-components';
import { Form, CheckboxProps } from 'semantic-ui-react';

import { isProduction } from '@/settings/environment';
import { CapyCaptcha, SetCapyValues } from '@/components/shared/CapyCaptcha';
import { Label } from '@/components/shared/Label';
import { SharedButton } from '@/components/shared/Button';

export type Values = {
  email: string;
  ignoreCapyCaptcha: boolean;
};

type Props = {
  errors: FormikErrors<Values>;
  handleChange: ((e: React.ChangeEvent<HTMLInputElement>) => void) &
    ((event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => void);
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  touched: FormikTouched<Values>;
  values: Values;
  capyError: boolean;
  setCapyValues: SetCapyValues;
};

export const FormContents: React.FC<Props> = ({
  errors,
  handleChange,
  handleSubmit,
  isLoading,
  touched,
  values,
  capyError,
  setCapyValues,
}) => {
  const { t } = useTranslation();
  return (
    <Styled>
      <form onSubmit={handleSubmit}>
        <div className="email-input">
          <Form.Input
            data-test="email-field"
            icon="mail"
            iconPosition="left"
            label={t('ui.email.label')}
            className={classnames({ error: errors.email && touched.email })}
            name="email"
            onChange={handleChange}
            placeholder={t('ui.email.placeholder')}
            value={values.email}
          />
          {touched.email && errors.email && (
            <div>
              <Label>{errors.email}</Label>
            </div>
          )}
        </div>
        <div className="capy-wrapper">
          <div>
            <CapyCaptcha onChange={setCapyValues} />
            <div>{t('ui.capyCaptcha.description')}</div>
            {capyError && <Label>{t('ui.capyCaptcha.error')}</Label>}
            {!isProduction() && (
              <Form.Checkbox
                label={t('ui.capyCaptcha.ignore')}
                id="ignoreCapyCaptcha"
                name="ignoreCapyCaptcha"
                data-test="ignore-capy-captcha"
                onChange={handleChange}
              />
            )}
          </div>
        </div>
        <div className="submit-button">
          <SharedButton dataTest="do-verify" disabled={isLoading} type="submit">
            {t('ui.action.submit')}
          </SharedButton>
        </div>
      </form>
    </Styled>
  );
};

const Styled = styled.div`
  .email-input {
    margin-bottom: 1.75em;
  }

  .verification-error {
    margin-top: 1.5em;
  }

  .capy-wrapper {
    margin: 0 auto 1.75em;
    text-align: center;
  }
`;
